import type { ChatOverview } from '../../types/whatsappChat';
import { lastActivityMs } from './chatListRules';

/**
 * Chat @lid doppie di una chat @c.us (stessa persona, due identificativi WhatsApp).
 * Modulo puro (nessun accesso a store, DB o WAHA).
 *
 * Segnalazione 4fb0265e (27/03): "alcune chat ci sono ma non sono mostrate, tipo
 * Fabio Merlo; l'ho trovato con la ricerca ma non era al posto giusto". Due cause:
 * 1. il confronto per nome toglieva una chat @lid se QUALSIASI chat @c.us aveva un
 *    nome di battesimo uguale o quasi ("Fabio Merlo" spariva perche' esisteva
 *    "Fabio Rossi"); la ricerca invece la trovava.
 * 2. togliendo il doppione @lid se ne perdeva l'ultimo messaggio: la chat @c.us
 *    restava ordinata con la sua data vecchia, quindi in fondo alla lista.
 */

function normName(name: string): string {
  return name.toLowerCase().trim().replace(/[.\s]+$/, '').replace(/\s+/g, ' ');
}

/**
 * Chat @c.us con lo stesso nome della chat @lid. Vale il nome identico oppure uno
 * che inizia con l'altro a parola intera ("Sara Risoli" / "Sara Risoli Obly"), ma
 * solo se la chat corrispondente e' UNA: con due candidati non si sa chi sia e la
 * chat @lid resta visibile. Niente piu' confronto sul solo nome di battesimo.
 */
export function matchCusByName(lidName: string | undefined | null, cusChats: Array<Pick<ChatOverview, 'id' | 'name'>>): string | null {
  if (!lidName) return null;
  const norm = normName(lidName);
  // Almeno nome e cognome: un nome singolo ("Fabio") e' troppo vago.
  if (norm.length < 4 || !norm.includes(' ')) return null;
  const startsWithWord = (long: string, short: string) =>
    long.startsWith(short) && (long.length === short.length || long[short.length] === ' ');
  const exact: string[] = [];
  const prefix: string[] = [];
  for (const c of cusChats) {
    if (!c.name) continue;
    const cus = normName(c.name);
    if (cus === norm) exact.push(c.id);
    else if (startsWithWord(cus, norm) || startsWithWord(norm, cus)) prefix.push(c.id);
  }
  if (exact.length === 1) return exact[0];
  if (exact.length === 0 && prefix.length === 1) return prefix[0];
  return null;
}

/**
 * La chat @c.us assorbe il doppione @lid: se l'ultimo messaggio piu' recente e' sul
 * @lid, la chat prende quello (anteprima, ordinamento, stato letto di WAHA).
 */
export function foldLidIntoCus(cus: ChatOverview, lid: ChatOverview): ChatOverview {
  if (lastActivityMs(lid) <= lastActivityMs(cus)) return cus;
  const folded: ChatOverview = { ...cus, lastMessage: lid.lastMessage };
  if ('wahaReadUpToMs' in lid) folded.wahaReadUpToMs = lid.wahaReadUpToMs;
  return folded;
}

/**
 * Toglie le chat @lid con un doppione @c.us noto (lidToCus) e ne porta l'ultimo
 * messaggio sulla chat @c.us. Le chat @lid senza doppione restano come sono.
 */
export function removeLidDuplicates(
  chats: ChatOverview[],
  lidToCus: (lid: ChatOverview) => string | null | undefined,
): { chats: ChatOverview[]; removed: number } {
  const byId = new Map(chats.map((c) => [c.id, c]));
  const folded = new Map<string, ChatOverview>();
  const dropped = new Set<string>();
  for (const c of chats) {
    if (!c.id.endsWith('@lid')) continue;
    const cusId = lidToCus(c);
    const cus = cusId ? folded.get(cusId) || byId.get(cusId) : undefined;
    if (!cusId || !cus || !cusId.endsWith('@c.us')) continue;
    folded.set(cusId, foldLidIntoCus(cus, c));
    dropped.add(c.id);
  }
  if (dropped.size === 0) return { chats, removed: 0 };
  const result = chats.filter((c) => !dropped.has(c.id)).map((c) => folded.get(c.id) || c);
  // Una chat @c.us che ha preso un messaggio piu' recente cambia posto.
  result.sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned) || lastActivityMs(b) - lastActivityMs(a));
  return { chats: result, removed: dropped.size };
}
